import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Play, Square, Loader2, Eye, EyeOff, Camera } from 'lucide-react';
import { Viewer } from '@ydrive/gs-viewer';
import * as THREE from 'three';

interface PathPose {
  time: number;
  camera_to_world: number[][];
}

interface GroundPath {
  points: number[][];
}

const MarketingTool: React.FC = () => {
  // Viewer refs
  const viewerRef = useRef<any | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // State
  const [loading, setLoading] = useState(false);
  const [modelLoading, setModelLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pathData, setPathData] = useState<{ ground: GroundPath; poses: PathPose[] } | null>(null);
  const [pathVisible, setPathVisible] = useState(false);
  const [imagePosesVisible, setImagePosesVisible] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [currentView, setCurrentView] = useState<'card' | '3d-viewer'>('card');
  
  // Auto-rotation state (from TheCollectiv3)
  const [isAutoRotating, setIsAutoRotating] = useState(false);
  const autoRotationRef = useRef<number | null>(null);
  const rotationAngleRef = useRef<number>(0);
  
  // Animation refs
  const animationRef = useRef<number | null>(null);
  const playbackStartRef = useRef<number>(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  
  // Three.js object refs
  const pathLineRef = useRef<THREE.Line | null>(null);
  const pathMarkersRef = useRef<THREE.Points | null>(null);
  const imagePosesLineRef = useRef<THREE.Line | null>(null);
  const imagePosesMarkersRef = useRef<THREE.Points | null>(null);

  const loadScene = async () => {
    try {
      setCurrentView('3d-viewer');
      setModelLoading(true);
      setError(null);

      await new Promise(resolve => setTimeout(resolve, 100));

      if (!containerRef.current) {
        throw new Error('Container not found');
      }

      console.log('[Marketing] Initializing viewer...');

      const viewer = new Viewer({
        sharedMemoryForWorkers: false,
        rootElement: containerRef.current,
        initialCameraPosition: [1.65, 0.42, -1.44],
        enableOptimizations: true,
      });
      
      viewerRef.current = viewer;

      console.log('[Marketing] Loading garden.gaus...');
      
      // Check if image_poses.json exists
      let hasImagePoses = false;
      try {
        const imagePosesCheck = await fetch('/image_poses.json', { method: 'HEAD' });
        hasImagePoses = imagePosesCheck.ok;
      } catch (e) {
        console.log('[Marketing] image_poses.json not available');
      }

      await viewer.addSplatScene('/garden.gaus', {
        showLoadingUI: false,
        progressiveLoad: false,
        ...(hasImagePoses && {
          imagePoses: '/image_poses.json',
          deferImagePoses: true,
        })
      });

      // ⬇️ NEW — grab the group the GAUS loader just added (normalization root)
const scene: THREE.Scene = (viewer as any).threeScene;
scene.updateMatrixWorld(true);

// naive last-child pick (works if addSplatScene appends). If you prefer, diff children before/after.
const splatRoot = scene.children[scene.children.length - 1];
splatRoot?.updateWorldMatrix(true, false);

// cache for later
(viewer as any)._splatRoot = splatRoot;
(viewer as any)._N = new THREE.Matrix4().copy(splatRoot.matrixWorld);

// (optional) name it for debugging
(splatRoot as any).name ||= 'SplatRoot';

      viewer.start();
      console.log('[Marketing] Scene loaded');

      // Load path data
      await loadPathData();

      setModelLoading(false);
    } catch (err) {
      console.error('[Marketing] Error loading scene:', err);
      setError(err instanceof Error ? err.message : 'Failed to load scene');
      setModelLoading(false);
    }
  };

  const loadPathData = async () => {
    try {
      console.log('[Marketing] Loading path data...');
      
      const [groundPathRes, pathPosesRes] = await Promise.all([
        fetch('/ground_path.json'),
        fetch('/path_poses.json'),
      ]);

      if (!groundPathRes.ok || !pathPosesRes.ok) {
        console.warn('[Marketing] Path data not available');
        return;
      }

      const groundPath: GroundPath = await groundPathRes.json();
      const pathPosesData = await pathPosesRes.json();
      const poses: PathPose[] = pathPosesData.poses;

      setPathData({ ground: groundPath, poses });
      console.log(`[Marketing] Loaded ${groundPath.points.length} ground points and ${poses.length} camera poses`);
    } catch (err) {
      console.warn('[Marketing] Could not load path data:', err);
    }
  };

  // Auto-rotation functions (from TheCollectiv3)
  const startAutoRotation = () => {
    if (!viewerRef.current) {
      console.error('[Marketing] No viewer for auto-rotation');
      return;
    }

    const viewer = viewerRef.current;
    const camera = viewer.camera;
    
    if (!camera) return;

    const initialPosition = {
      x: camera.position.x,
      y: camera.position.y,
      z: camera.position.z
    };

    const radius = Math.sqrt(initialPosition.x * initialPosition.x + initialPosition.z * initialPosition.z);
    const centerY = initialPosition.y;
    const rotationSpeed = 0.005;
    
    console.log('[Marketing] Starting auto-rotation');
    setIsAutoRotating(true);
    rotationAngleRef.current = Math.atan2(initialPosition.z, initialPosition.x);
    
    const rotate = () => {
      if (!autoRotationRef.current || !viewerRef.current) return;
      
      rotationAngleRef.current += rotationSpeed;
      
      const newX = Math.cos(rotationAngleRef.current) * radius;
      const newZ = Math.sin(rotationAngleRef.current) * radius;
      const newY = centerY;
      
      camera.position.set(newX, newY, newZ);
      camera.lookAt(0, centerY * 0.5, 0);
      
      autoRotationRef.current = requestAnimationFrame(rotate);
    };
    
    autoRotationRef.current = requestAnimationFrame(rotate);
  };

  const stopAutoRotation = () => {
    console.log('[Marketing] Stopping auto-rotation');
    if (autoRotationRef.current) {
      cancelAnimationFrame(autoRotationRef.current);
      autoRotationRef.current = null;
    }
    setIsAutoRotating(false);
  };

  const toggleAutoRotation = () => {
    if (isAutoRotating) {
      stopAutoRotation();
    } else {
      startAutoRotation();
    }
  };

  const togglePathVisibility = () => {
    if (!viewerRef.current || !pathData) return;

    const viewer = viewerRef.current;
     const scene: THREE.Scene = viewer.threeScene;
  const splatRoot: THREE.Object3D = (viewer as any)._splatRoot ?? scene;

  if (!splatRoot) {
    console.error('[Marketing] Could not access splat root or scene');
    return;
  }
    

    if (pathVisible) {
      console.log('[Marketing] Hiding ground path');
      if (pathLineRef.current) {
        scene.remove(pathLineRef.current);
        pathLineRef.current.geometry.dispose();
        (pathLineRef.current.material as THREE.Material).dispose();
        pathLineRef.current = null;
      }
      if (pathMarkersRef.current) {
        scene.remove(pathMarkersRef.current);
        pathMarkersRef.current.geometry.dispose();
        (pathMarkersRef.current.material as THREE.Material).dispose();
        pathMarkersRef.current = null;
      }
      setPathVisible(false);
    } else {
      console.log('[Marketing] Showing ground path');
      const points = pathData.ground.points.map(
        ([x, y, z]) => new THREE.Vector3(x, y, z)
      );

      // Green line for ground path
      const lineGeometry = new THREE.BufferGeometry().setFromPoints(points);
      const lineMaterial = new THREE.LineBasicMaterial({
        color: 0x00ff00, // Green
        linewidth: 3,
      });
      const line = new THREE.Line(lineGeometry, lineMaterial);
      scene.add(line);
      pathLineRef.current = line;

      // Yellow markers for ground path
      const positions = new Float32Array(points.length * 3);
      points.forEach((p, i) => {
        positions[3 * i] = p.x;
        positions[3 * i + 1] = p.y;
        positions[3 * i + 2] = p.z;
      });
      const markerGeometry = new THREE.BufferGeometry();
      markerGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      const markerMaterial = new THREE.PointsMaterial({
        color: 0xffff00, // Yellow
        size: 0.1,
        sizeAttenuation: true,
      });
      const markers = new THREE.Points(markerGeometry, markerMaterial);
      scene.add(markers);
      pathMarkersRef.current = markers;

      setPathVisible(true);
    }
  };

  const arr4x4ToMat = (A: number[][]) =>
  new THREE.Matrix4().set(
    A[0][0], A[0][1], A[0][2], A[0][3],
    A[1][0], A[1][1], A[1][2], A[1][3],
    A[2][0], A[2][1], A[2][2], A[2][3],
    0,0,0,1
  );

const matToArr4x4 = (M: THREE.Matrix4): number[][] => {
  const e = M.elements;
  return [
    [e[0],e[4],e[8], e[12]],
    [e[1],e[5],e[9], e[13]],
    [e[2],e[6],e[10],e[14]],
    [0,0,0,1]
  ];
};

  const toggleImagePosesVisibility = () => {
    if (!viewerRef.current || !pathData) return;

    const viewer = viewerRef.current;
     const scene: THREE.Scene = viewer.threeScene;
  const splatRoot: THREE.Object3D = (viewer as any)._splatRoot ?? scene;

  if (!splatRoot) {
    console.error('[Marketing] Could not access splat root or scene');
    return;
  }

    if (imagePosesVisible) {
      console.log('[Marketing] Hiding image poses path');
      if (imagePosesLineRef.current) {
        scene.remove(imagePosesLineRef.current);
        imagePosesLineRef.current.geometry.dispose();
        (imagePosesLineRef.current.material as THREE.Material).dispose();
        imagePosesLineRef.current = null;
      }
      if (imagePosesMarkersRef.current) {
        scene.remove(imagePosesMarkersRef.current);
        imagePosesMarkersRef.current.geometry.dispose();
        (imagePosesMarkersRef.current.material as THREE.Material).dispose();
        imagePosesMarkersRef.current = null;
      }
      setImagePosesVisible(false);
    } else {
      console.log('[Marketing] Showing image poses path');
      
      // Extract camera positions from path_poses (camera_to_world matrices)
      const posePoints = pathData.poses.map(pose => {
        // Camera position is in the last column of the transformation matrix
        return new THREE.Vector3(
          pose.camera_to_world[0][3],
          pose.camera_to_world[1][3],
          pose.camera_to_world[2][3]
        );
      });

      // Cyan/Blue line for image poses path
      const lineGeometry = new THREE.BufferGeometry().setFromPoints(posePoints);
      const lineMaterial = new THREE.LineBasicMaterial({
        color: 0x00ffff, // Cyan
        linewidth: 3,
      });
      const line = new THREE.Line(lineGeometry, lineMaterial);
      scene.add(line);
      imagePosesLineRef.current = line;

      // Magenta markers for image poses
      const positions = new Float32Array(posePoints.length * 3);
      posePoints.forEach((p, i) => {
        positions[3 * i] = p.x;
        positions[3 * i + 1] = p.y;
        positions[3 * i + 2] = p.z;
      });
      const markerGeometry = new THREE.BufferGeometry();
      markerGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      const markerMaterial = new THREE.PointsMaterial({
        color: 0xff00ff, // Magenta
        size: 0.12,
        sizeAttenuation: true,
      });
      const markers = new THREE.Points(markerGeometry, markerMaterial);
      scene.add(markers);
      imagePosesMarkersRef.current = markers;

      setImagePosesVisible(true);
    }
  };

  const setCameraFromMatrix = (matrixWorld: number[][]) => {
  if (!viewerRef.current) return;
  const viewer = viewerRef.current;

  const camera: THREE.Camera = viewer.camera;
  if (!camera) return;

  // ⬇️ NEW — normalization from GAUS root
  const N: THREE.Matrix4 | undefined = (viewer as any)._N;
  const T_world = arr4x4ToMat(matrixWorld);
  const T_view = N ? new THREE.Matrix4().multiplyMatrices(N, T_world) : T_world;

  camera.matrixAutoUpdate = false;
  camera.matrix.copy(T_view);

  const pos = new THREE.Vector3();
  const quat = new THREE.Quaternion();
  const scl = new THREE.Vector3();
  camera.matrix.decompose(pos, quat, scl);

  camera.position.copy(pos);
  (camera as any).quaternion.copy(quat);
  camera.updateMatrixWorld(true);

  (viewer as any).forceRenderNextFrame?.();
};

  const interpolatePose = (t: number): number[][] => {
    if (!pathData || pathData.poses.length === 0) {
      return [[1,0,0,0],[0,1,0,0],[0,0,1,0],[0,0,0,1]];
    }

    const poses = pathData.poses;
    const t0 = poses[0].time;
    const tN = poses[poses.length - 1].time;

    if (t <= t0) return poses[0].camera_to_world;
    if (t >= tN) return poses[poses.length - 1].camera_to_world;

    let i = 1;
    while (i < poses.length && poses[i].time < t) i++;

    const poseA = poses[i - 1];
    const poseB = poses[i];
    const u = (t - poseA.time) / Math.max(1e-6, poseB.time - poseA.time);

    const matA = new THREE.Matrix4().set(
      poseA.camera_to_world[0][0], poseA.camera_to_world[0][1], poseA.camera_to_world[0][2], poseA.camera_to_world[0][3],
      poseA.camera_to_world[1][0], poseA.camera_to_world[1][1], poseA.camera_to_world[1][2], poseA.camera_to_world[1][3],
      poseA.camera_to_world[2][0], poseA.camera_to_world[2][1], poseA.camera_to_world[2][2], poseA.camera_to_world[2][3],
      0, 0, 0, 1
    );

    const matB = new THREE.Matrix4().set(
      poseB.camera_to_world[0][0], poseB.camera_to_world[0][1], poseB.camera_to_world[0][2], poseB.camera_to_world[0][3],
      poseB.camera_to_world[1][0], poseB.camera_to_world[1][1], poseB.camera_to_world[1][2], poseB.camera_to_world[1][3],
      poseB.camera_to_world[2][0], poseB.camera_to_world[2][1], poseB.camera_to_world[2][2], poseB.camera_to_world[2][3],
      0, 0, 0, 1
    );

    const posA = new THREE.Vector3();
    const quatA = new THREE.Quaternion();
    const scaleA = new THREE.Vector3();
    matA.decompose(posA, quatA, scaleA);

    const posB = new THREE.Vector3();
    const quatB = new THREE.Quaternion();
    const scaleB = new THREE.Vector3();
    matB.decompose(posB, quatB, scaleB);

    const pos = new THREE.Vector3().lerpVectors(posA, posB, u);
    const quat = new THREE.Quaternion().copy(quatA).slerp(quatB, u);
    const scale = new THREE.Vector3().lerpVectors(scaleA, scaleB, u);

    const interpolated = new THREE.Matrix4().compose(pos, quat, scale);
    const e = interpolated.elements;

    return [
      [e[0], e[4], e[8], e[12]],
      [e[1], e[5], e[9], e[13]],
      [e[2], e[6], e[10], e[14]],
      [0, 0, 0, 1]
    ];
  };

  const playWalkthrough = () => {
    if (!pathData || isPlaying) return;

    console.log('[Marketing] Starting walkthrough');
    setIsPlaying(true);
    playbackStartRef.current = performance.now() / 1000;

    const poses = pathData.poses;
    const t0 = poses[0].time;
    const tN = poses[poses.length - 1].time;

    const animate = () => {
      const now = performance.now() / 1000;
      const elapsed = now - playbackStartRef.current;
      const t = t0 + elapsed;

      setCurrentTime(elapsed);

      if (t >= tN) {
        console.log('[Marketing] Walkthrough complete');
        setIsPlaying(false);
        setCurrentTime(0);
        if (isRecording) {
          stopRecording();
        }
        return;
      }

      const poseMatrix = interpolatePose(t);
      setCameraFromMatrix(poseMatrix);

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);
  };

  const stopWalkthrough = () => {
  if (animationRef.current) {
    cancelAnimationFrame(animationRef.current);
    animationRef.current = null;
  }
  
  // Re-enable automatic camera matrix updates for user controls
  if (viewerRef.current?.camera) {
    viewerRef.current.camera.matrixAutoUpdate = true;
  }
  
  setIsPlaying(false);
  setCurrentTime(0);
};

  const startRecording = async () => {
    if (!containerRef.current || !viewerRef.current) {
      alert('Viewer not ready');
      return;
    }

    try {
      const canvas = containerRef.current.querySelector('canvas');
      if (!canvas) throw new Error('Canvas not found');

      const stream = canvas.captureStream(30);
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'video/webm;codecs=vp9',
        videoBitsPerSecond: 8000000,
      });

      mediaRecorderRef.current = mediaRecorder;
      recordedChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        downloadRecording();
      };

      mediaRecorder.start(1000);
      setIsRecording(true);
    } catch (err) {
      console.error('[Marketing] Recording failed:', err);
      alert('Failed to start recording');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const downloadRecording = () => {
    if (recordedChunksRef.current.length === 0) return;

    const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    link.download = `garden-${timestamp}.webm`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    recordedChunksRef.current = [];
  };

  const recordCircular = async () => {
    await startRecording();
    setTimeout(() => {
      if (isRecording) startAutoRotation();
    }, 500);
  };

  const recordWalkthrough = async () => {
    await startRecording();
    setTimeout(() => {
      if (isRecording) playWalkthrough();
    }, 500);
  };

  const captureCurrentPosition = () => {
    if (!viewerRef.current) return;
    const camera = viewerRef.current.camera;
    if (camera && camera.position) {
      const pos = camera.position;
      const coords = [
        Math.round(pos.x * 100) / 100,
        Math.round(pos.y * 100) / 100,
        Math.round(pos.z * 100) / 100
      ];
      alert(`Camera position: [${coords.join(', ')}]`);
      console.log('[Marketing] Camera position:', coords);
    }
  };

 const resetCameraPosition = () => {
  if (!viewerRef.current) return;
  const camera = viewerRef.current.camera;
  if (camera) {
    // Re-enable automatic updates first
    camera.matrixAutoUpdate = true;
    
    // Reset to initial position
    camera.position.set(1.65, 0.42, -1.44);
    // Look at origin
    camera.lookAt(0, 0, 0);
    camera.updateMatrixWorld(true);
    
    console.log('[Marketing] Camera reset to initial position [1.65, 0.42, -1.44]');
  }
};

  const closeViewer = () => {
  stopAutoRotation();
  stopWalkthrough();
  stopRecording();

  if (viewerRef.current) {
    // ⬇️ Use splatRoot if available, otherwise fallback to threeScene
    const splatRoot: THREE.Object3D =
      (viewerRef.current as any)._splatRoot ?? viewerRef.current.threeScene;

    if (pathLineRef.current) {
      splatRoot.remove(pathLineRef.current);
      pathLineRef.current.geometry.dispose();
      (pathLineRef.current.material as THREE.Material).dispose();
      pathLineRef.current = null;
    }
    if (pathMarkersRef.current) {
      splatRoot.remove(pathMarkersRef.current);
      pathMarkersRef.current.geometry.dispose();
      (pathMarkersRef.current.material as THREE.Material).dispose();
      pathMarkersRef.current = null;
    }
    if (imagePosesLineRef.current) {
      splatRoot.remove(imagePosesLineRef.current);
      imagePosesLineRef.current.geometry.dispose();
      (imagePosesLineRef.current.material as THREE.Material).dispose();
      imagePosesLineRef.current = null;
    }
    if (imagePosesMarkersRef.current) {
      splatRoot.remove(imagePosesMarkersRef.current);
      imagePosesMarkersRef.current.geometry.dispose();
      (imagePosesMarkersRef.current.material as THREE.Material).dispose();
      imagePosesMarkersRef.current = null;
    }

    if (typeof viewerRef.current.dispose === 'function') {
      viewerRef.current.dispose();
      viewerRef.current = null;
    }
  }

  setCurrentView('card');
  setModelLoading(false);
  setPathData(null);
  setPathVisible(false);
  setImagePosesVisible(false);
};

  useEffect(() => {
    return () => {
      if (viewerRef.current) {
        viewerRef.current.dispose();
      }
    };
  }, []);

  // Render card view
  if (currentView === 'card') {
    return (
      <div className="min-h-screen bg-[#161513]">
        <header className="bg-[#161513] border-b border-[#0CB4E4]/20 p-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <h1 className="text-2xl font-bold text-white">Marketing Tool - Garden Scene</h1>
            <div className="text-sm text-[#0CB4E4]">Testing Ground</div>
          </div>
        </header>

        <div className="max-w-7xl mx-auto p-6">
          <div className="max-w-md mx-auto">
            <div className="bg-[#161513] rounded-xl overflow-hidden border border-[#0CB4E4]/30 hover:border-[#0CB4E4]/60 transition-all cursor-pointer"
                 onClick={loadScene}>
              <div className="h-64 bg-gradient-to-br from-[#0CB4E4]/20 via-[#0CB4E4]/10 to-[#0CB4E4]/20 flex items-center justify-center">
                <div className="text-center">
                  <Play className="w-16 h-16 text-[#0CB4E4] mx-auto mb-4" />
                  <p className="text-white text-lg font-medium">Click to Load Garden Scene</p>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-white mb-2">Garden Walkthrough</h3>
                <p className="text-gray-400 text-sm mb-4">
                  Interactive 3D garden scene with ground path visualization and walkthrough recording capabilities
                </p>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span>3D Scene</span>
                  <span>•</span>
                  <span>Path Recording</span>
                  <span>•</span>
                  <span>Interactive</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render 3D viewer
  return (
    <div className="fixed inset-0 w-full h-full bg-black z-[999]">
      {/* Close button */}
      <button
        onClick={closeViewer}
        className="fixed top-4 right-4 z-[1001] bg-[#161513]/90 backdrop-blur-sm text-white px-6 py-3 rounded-xl border border-[#0CB4E4]/30 hover:bg-[#161513] transition-all"
      >
        ✕ Close (ESC)
      </button>

      {/* Loading overlay */}
      {modelLoading && (
        <div className="fixed inset-0 z-[1002] bg-black/80 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-[#0CB4E4] mx-auto mb-4" />
            <p className="text-white text-lg">Loading garden scene...</p>
          </div>
        </div>
      )}

      {/* Viewer container */}
      <div ref={containerRef} className="absolute inset-0 w-full h-full" />

      {/* Controls */}
      {!modelLoading && (
        <>
          {/* Left controls */}
          <div className="fixed bottom-4 left-4 z-[1001] flex flex-col gap-3">
            {/* Auto-rotation */}
            <button
              onClick={toggleAutoRotation}
              className="bg-[#161513]/90 backdrop-blur-sm text-white p-4 rounded-xl border border-[#0CB4E4]/30 hover:bg-[#161513] transition-all flex items-center gap-3"
            >
              <div className={`w-6 h-6 ${isAutoRotating ? 'animate-spin text-[#0CB4E4]' : 'text-[#0CB4E4]'}`}>
                🔄
              </div>
              <div className="text-sm">
                <div className="font-medium">Auto Rotate</div>
                <div className="text-xs text-gray-400">{isAutoRotating ? 'Rotating...' : 'Click to rotate'}</div>
              </div>
            </button>

            {/* Record circular */}
            <button
              onClick={isRecording || isAutoRotating ? () => { stopAutoRotation(); stopRecording(); } : recordCircular}
              className={`backdrop-blur-sm text-white p-4 rounded-xl border transition-all flex items-center gap-3 ${
                isRecording || isAutoRotating ? 'bg-red-600/90 border-red-500/30' : 'bg-[#161513]/90 border-[#0CB4E4]/30 hover:bg-[#161513]'
              }`}
            >
              <span className="text-lg">{isRecording ? '⏹️' : '🎥'}</span>
              <div className="text-sm">
                <div className="font-medium">{isRecording ? 'Stop Recording' : 'Record Circular'}</div>
                <div className="text-xs text-gray-400">{isRecording ? 'Recording...' : 'Circular motion'}</div>
              </div>
            </button>

            {/* Path visualization */}
            {pathData && (
              <button
                onClick={togglePathVisibility}
                className="bg-[#161513]/90 backdrop-blur-sm text-white p-4 rounded-xl border border-[#0CB4E4]/30 hover:bg-[#161513] transition-all flex items-center gap-3"
              >
                {pathVisible ? <EyeOff className="w-6 h-6 text-green-400" /> : <Eye className="w-6 h-6 text-green-400" />}
                <div className="text-sm">
                  <div className="font-medium">{pathVisible ? 'Hide' : 'Show'} Ground Path</div>
                  <div className="text-xs text-gray-400">Green line • {pathData.ground.points.length} pts</div>
                </div>
              </button>
            )}

            {/* Image poses visualization */}
            {pathData && (
              <button
                onClick={toggleImagePosesVisibility}
                className="bg-[#161513]/90 backdrop-blur-sm text-white p-4 rounded-xl border border-[#0CB4E4]/30 hover:bg-[#161513] transition-all flex items-center gap-3"
              >
                {imagePosesVisible ? <EyeOff className="w-6 h-6 text-cyan-400" /> : <Eye className="w-6 h-6 text-cyan-400" />}
                <div className="text-sm">
                  <div className="font-medium">{imagePosesVisible ? 'Hide' : 'Show'} Image Poses</div>
                  <div className="text-xs text-gray-400">Cyan line • {pathData.poses.length} poses</div>
                </div>
              </button>
            )}

            {/* Walkthrough playback */}
            {pathData && (
              <button
                onClick={isPlaying ? stopWalkthrough : playWalkthrough}
                disabled={!pathData}
                className="bg-[#161513]/90 backdrop-blur-sm text-white p-4 rounded-xl border border-[#0CB4E4]/30 hover:bg-[#161513] transition-all flex items-center gap-3 disabled:opacity-50"
              >
                {isPlaying ? <Square className="w-6 h-6 text-[#0CB4E4]" /> : <Play className="w-6 h-6 text-[#0CB4E4]" />}
                <div className="text-sm">
                  <div className="font-medium">{isPlaying ? 'Stop' : 'Play'} Walkthrough</div>
                  <div className="text-xs text-gray-400">
                    {isPlaying ? `${currentTime.toFixed(1)}s` : 'Path playback'}
                  </div>
                </div>
              </button>
            )}

            {/* Record walkthrough */}
            {pathData && (
              <button
                onClick={recordWalkthrough}
                disabled={!pathData || isPlaying || isRecording}
                className="bg-[#0CB4E4]/90 backdrop-blur-sm text-[#161513] p-4 rounded-xl border border-[#0CB4E4]/30 hover:bg-[#0CB4E4] transition-all flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                <Play className="w-6 h-6" />
                <div className="text-sm">
                  <div className="font-medium">Record Walkthrough</div>
                  <div className="text-xs opacity-80">Along ground path</div>
                </div>
              </button>
            )}
          </div>

          
        </>
      )}
      {/* Right controls */}
      <div className="fixed bottom-4 right-4 z-[1001] flex flex-col gap-3">
        <button
              onClick={captureCurrentPosition}
              className="bg-[#161513]/90 backdrop-blur-sm text-white p-4 rounded-xl border border-[#0CB4E4]/30 hover:bg-[#161513] transition-all flex items-center gap-3"
            >
              <Camera className="w-6 h-6 text-[#0CB4E4]" />
              <div className="text-sm">
                <div className="font-medium">Get Camera Position</div>
                <div className="text-xs text-gray-400">Save coordinates</div>
              </div>
            </button>
            <button
              onClick={resetCameraPosition}
              className="bg-[#161513]/90 backdrop-blur-sm text-white p-4 rounded-xl border border-[#0CB4E4]/30 hover:bg-[#161513] transition-all flex items-center gap-3"
            >
              <span className="text-xl">🔄</span>
              <div className="text-sm">
                <div className="font-medium">Reset Camera</div>
                <div className="text-xs text-gray-400">Back to start</div>
              </div>
            </button>
      </div>
    </div>
  );
};

export default MarketingTool;